import * as React from "react";
import type { NumberFormatValues } from "react-number-format";
import { useSearchParams } from "react-router-dom";
import type { WrappedFieldProps } from "redux-form";
import { Field, Fields } from "redux-form";
import { isEmpty } from "underscore";

import {
  GraterOrEqualZeroIntegerNumberFormat,
  Popover,
  PopoverAnchorButton,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { DollarIcon } from "@braintrust/braintrust-ui-components/Icons";
import { RateForm } from "@js/apps/common/components/filters/config";
import { useHandleFilterApplied } from "@js/apps/freelancer/hooks/use-handle-filter-applied";
import { OptionTileButtons } from "@js/components/option-tile-buttons";
import { DollarNumberField } from "@js/forms/fields";
import { PossibleFilters } from "@js/types/common";

import { JobPopoverFilterButton } from "../../components";
import {
  CUSTOM_RATE_VALUE,
  isRateOptionSelected,
  RATE_OPTIONS,
} from "../../const";

import styles from "./style.module.scss";

const formId = "job-filters__rate";

export const RateFilter = () => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [params] = useSearchParams();
  const { handleFilterApplied } = useHandleFilterApplied();
  const handleSubmitSideAction = (values: Record<string, unknown>) => {
    void handleFilterApplied({
      filter_type: PossibleFilters.RATE,
      filter_value: values,
    });
  };

  const hourlyBudgetMinimum = params.get("hourly_budget_minimum_usd");
  const isActive = Boolean(hourlyBudgetMinimum);

  const buttonLabel = isActive ? `$${hourlyBudgetMinimum}/hr` : "Rate";

  return (
    <JobPopoverFilterButton
      isOpen={isOpen}
      startIcon={<DollarIcon viewBox="-6 3 20 20" />}
      setIsOpen={setIsOpen}
      label={buttonLabel}
      isActive={isActive}
      popoverContent={
        <RateForm
          form={formId}
          onSubmitSuccess={() => {
            setIsOpen(false);
          }}
          onSubmitSideAction={handleSubmitSideAction}
        >
          {({ submit }) => {
            return (
              <Fields
                submit={submit}
                names={["hourly_budget_minimum_usd", "custom_rate"]}
                component={RateField}
              />
            );
          }}
        </RateForm>
      }
    />
  );
};

type RateFieldProps = {
  submit: () => void;
  hourly_budget_minimum_usd: WrappedFieldProps;
  custom_rate: WrappedFieldProps;
};

const RateField = (props: RateFieldProps) => {
  const { submit, hourly_budget_minimum_usd, custom_rate } = props;

  const minBudget = hourly_budget_minimum_usd.input.value;

  const isCustomRate = React.useMemo(() => {
    return (
      minBudget === CUSTOM_RATE_VALUE &&
      custom_rate.input.value &&
      isEmpty(isRateOptionSelected(minBudget))
    );
  }, [minBudget, custom_rate.input.value]);

  const handleChange = (newRateValue: number | null) => {
    hourly_budget_minimum_usd.input.onChange(newRateValue);
  };

  const handleReset = () => {
    hourly_budget_minimum_usd.input.onChange(null);
    custom_rate.input.onChange(null);
  };

  return (
    <JobPopoverFilterButton.Content
      sx={{
        maxWidth: "initial !important",
      }}
      onReset={handleReset}
      onApply={submit}
    >
      <OptionTileButtons
        value={minBudget}
        onChange={(rate) => handleChange(rate)}
        options={RATE_OPTIONS}
        canSingleValueBeEmpty
        selectedClassName="selected"
        className={styles.button}
        renderCustomOptionButton={() => {
          return (
            <CustomRatePopover
              rateValue={custom_rate.input.value}
              onCustomRateClick={() => {
                hourly_budget_minimum_usd.input.onChange(CUSTOM_RATE_VALUE);
              }}
              isCustomRate={isCustomRate}
            />
          );
        }}
      />
    </JobPopoverFilterButton.Content>
  );
};

type CustomRatePopoverProps = {
  rateValue: number;
  isCustomRate: boolean;
  onCustomRateClick: () => void;
};

const CustomRatePopover = ({
  rateValue,
  isCustomRate,
  onCustomRateClick,
}: CustomRatePopoverProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const getPopoverButtonLabel = () => {
    if (isCustomRate && rateValue) {
      return `$${rateValue}/hr`;
    }

    return "Custom";
  };

  return (
    <Popover
      open={isOpen}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
      anchor={
        <PopoverAnchorButton
          selectedClassName="selected"
          isOpen={isOpen}
          ref={buttonRef}
          onClick={onCustomRateClick}
          isSelected={isCustomRate}
        >
          {getPopoverButtonLabel()}
        </PopoverAnchorButton>
      }
      slotProps={{
        paper: {
          sx: {
            padding: "16px",
            border: "1px solid var(--grey-4)",
          },
        },
      }}
    >
      <Typography variant="paragraph" component="p" size="small">
        Enter minimum hourly rate
      </Typography>
      <Field
        id="custom_rate"
        name="custom_rate"
        className={styles.input}
        placeholder="100"
        component={DollarNumberField}
        NumberFormat={GraterOrEqualZeroIntegerNumberFormat}
        slotProps={{
          htmlInput: {
            isAllowed: checkLimit,
            onKeyPress: (ev: React.KeyboardEvent) => {
              if (ev.key === "Enter") {
                setIsOpen(false);
              }
            },
          },
        }}
      />
    </Popover>
  );
};

const MAX_RATE_VALUE = 1000;
const MIN_RATE_VALUE = 0;
const checkLimit = (values: NumberFormatValues) => {
  if (!values.floatValue && values.floatValue !== MIN_RATE_VALUE) {
    return true;
  }

  return (
    values.floatValue > MIN_RATE_VALUE && values.floatValue < MAX_RATE_VALUE
  );
};
